import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from "react";
import PropTypes from "prop-types";
import {
  HamburgerButton,
  useMediaQuery
} from "discover.medical.react-component-library";
import throttle from "lodash/throttle";

import { useConfig } from "@eis-poc/lib-discover-medical-mfes";
import { isScrolled } from "../../utils/isScrolled";
import { useLocation, Link } from "react-router-dom";
import MfeWrapper from "../MfeWrapper";
import {
  StyledHeader,
  StyledAppBar,
  StyledFlex,
  StyledHeaderLogo,
  StyledSearchButton,
  StyledSearchButtonIcon,
  StyledSearchTray,
  StyledDesktopSearchBox,
  StyledHeaderTools,
  StyledHamburgerWrapper,
  StyleHeaderContent
} from "./Header.styled";

import { NavigationMenu } from "./components/NavigationMenu";
import HamburgerMenu from "./components/HamburgerMenu";

import { getThemeFriendlyProductCode } from "../../utils/productCodeUtils";
import AppSwitcher from "./components/AppSwitcher";
import { NAV_BREAKPOINT } from "../../utils/constants";

const safeConfig = { header: {}, headerFallbacks: {}, mfes: {} };

const Header = ({ eventBus }) => {
  const appConfig = useConfig();
  const location = useLocation();

  const configData = useMemo(
    () => ({ ...safeConfig, ...appConfig }),
    [safeConfig, appConfig]
  );

  const headerData = configData.header;
  const headerFallbacks = configData.headerFallbacks;
  const { logosFallBack } = headerFallbacks;
  const { navLinks, logo, appSwitcherData, isShowSearchWidget } = headerData;
  const logos = logo !== undefined ? logo : logosFallBack;
  const productCode = getThemeFriendlyProductCode(configData);
  const checkIfMfeExist = useCallback(
    (componentName) => {
      return Object.keys(configData.mfes).includes(componentName);
    },
    [configData]
  );

  // keep track of header so we can identify if user clicks outside of it
  const mfewHeaderRef = useRef(null);
  const [isHamburgerOpen, setHamburgerOpen] = useState(false);
  const [isPageScrolled, setPageScrolled] = useState(false);
  const [isMobileSearchOpen, setMobileSearchOpen] = useState(false);

  const isMobile = useMediaQuery("max", "sm");
  const isTablet = useMediaQuery("min", "sm");
  const isDesktop = useMediaQuery("min", "md");
  const showPrimaryNav = useMediaQuery("min", NAV_BREAKPOINT);

  const handleScroll = () => {
    // when user scrolls down the page a certain amount, we consider the page to be in the "scrolled" state.
    // With this state we can do things such as hide the navigation menu, and shrink the app bar
    setPageScrolled(isScrolled());
  };
  const throttledHandleScroll = throttle(handleScroll, 200);

  useEffect(() => {
    window.addEventListener("scroll", throttledHandleScroll);
    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
    };
  }, []); // the empty array here signifies "only run this effect once, on load"

  useEffect(() => {
    setMobileSearchOpen(false);
  }, [location.pathname, location.search]);

  return (
    <StyledHeader ref={mfewHeaderRef} className="bui-fixed">
      {appSwitcherData && !isPageScrolled && (
        <AppSwitcher appSwitcherData={appSwitcherData} />
      )}
      <StyledAppBar $isPageScrolled={isPageScrolled}>
        <StyledFlex
          container
          noGap
          align={{ content: "center", item: "center" }}
          justify={{ content: "space-between" }}
        >
          <StyleHeaderContent cell align={{ item: "center" }}>
            <StyledHamburgerWrapper
              data-testid="hamburger-wrapper"
              $isPageScrolled={isPageScrolled}
            >
              <HamburgerButton
                isOpen={isHamburgerOpen}
                onClick={() => {
                  setHamburgerOpen(!isHamburgerOpen);
                  setMobileSearchOpen(false);
                }}
              />
              <HamburgerMenu
                links={navLinks}
                isOpen={isHamburgerOpen}
                setOpen={setHamburgerOpen}
                pathname={location.pathname}
                eventBus={eventBus}
              />
            </StyledHamburgerWrapper>
            <Link to="/">
              <StyledHeaderLogo
                $logo={logos}
                src={
                  isMobile ? logos?.mobileLogo?.src : logos?.desktopLogo?.src
                }
                alt={
                  isMobile ? logos?.mobileLogo?.alt : logos?.desktopLogo?.alt
                }
                onClick={() => {
                  setHamburgerOpen(false);
                }}
                data-testid="logo"
              />
            </Link>
            {location.pathname !== "/" && isShowSearchWidget && (
              <StyledDesktopSearchBox data-testid="search-bar-fragment">
                {checkIfMfeExist("PlatformSearchBar") && (
                  <MfeWrapper
                    name={"PlatformSearchBar"}
                    size={isPageScrolled ? "sm" : "md"}
                    eventBus={eventBus}
                  />
                )}
              </StyledDesktopSearchBox>
            )}
          </StyleHeaderContent>
          <StyledHeaderTools cell align={{ item: "center" }}>
            <div data-testid="user-header-fragment">
              <MfeWrapper
                name={"PlatformPersonalization"}
                productCode={productCode}
                eventBus={eventBus}
              />
            </div>
            {checkIfMfeExist("PlatformCEButtonWidget") && (
              <MfeWrapper name={"PlatformCEButtonWidget"} eventBus={eventBus} />
            )}
            {location.pathname !== "/" && isShowSearchWidget && (
              <StyledSearchButton
                isOverlayOpen={isMobileSearchOpen}
                padded={false}
                background={false}
                onClick={() => {
                  setMobileSearchOpen(!isMobileSearchOpen);
                  setHamburgerOpen(false);
                }}
                size={isTablet ? "md" : "sm"}
                data-testid="search-toggle"
                label="toggle search menu"
              >
                <StyledSearchButtonIcon
                  icon={{
                    glyph: `${isMobileSearchOpen ? "close" : "search"}`,
                    color: "white",
                    size: isTablet ? "lg" : "md"
                  }}
                  data-testid={
                    isMobileSearchOpen
                      ? "closeSearchMenu-icon"
                      : "openSearchMenu-icon"
                  }
                />
              </StyledSearchButton>
            )}
          </StyledHeaderTools>
        </StyledFlex>
      </StyledAppBar>

      {showPrimaryNav && (
        <NavigationMenu
          links={navLinks}
          isHidden={isPageScrolled}
          pathname={location.pathname}
          eventBus={eventBus}
        />
      )}

      {!isDesktop && isShowSearchWidget && (
        <StyledSearchTray
          isHidden={!isMobileSearchOpen}
          data-testid="mobile-search-tray"
        >
          <MfeWrapper
            data-testid="mobile-only-search-box"
            size="sm"
            name={"PlatformSearchBar"}
            eventBus={eventBus}
          />
        </StyledSearchTray>
      )}
    </StyledHeader>
  );
};

Header.propTypes = {
  eventBus: PropTypes.object
};

export default Header;
